/**
 * Camera barcode scanner overlay for the Admin product forms.
 *
 * A trimmed sibling of the POS `CameraBarcodeScanner`: same html5-qrcode engine
 * and formats, but without the POS-only extras (image match, recent-scan
 * sidebar, continuous mode) that a product form has no use for. It lives in
 * apps/admin because the POS component imports POS services.
 *
 * Decodes one barcode, hands it back, and closes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@bbpos/ui';
import { cn } from '@/constants/design-tokens';
import { cameraErrorMessage, isAcceptableScan, normalizeScannedBarcode } from './barcode-scan-logic';

const SCANNER_ELEMENT_ID = 'bbpos-admin-barcode-scanner';

const SUPPORTED_FORMATS = [
  Html5QrcodeSupportedFormats.EAN_13,
  Html5QrcodeSupportedFormats.EAN_8,
  Html5QrcodeSupportedFormats.UPC_A,
  Html5QrcodeSupportedFormats.UPC_E,
  Html5QrcodeSupportedFormats.CODE_128,
  Html5QrcodeSupportedFormats.CODE_39,
  Html5QrcodeSupportedFormats.QR_CODE,
];

function CloseIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CameraOffIcon() {
  return (
    <svg className="h-16 w-16 text-neutral-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 1l22 22M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3l2-3h6l2 3h3a2 2 0 012 2v9.34" />
      <path d="M14.12 14.12A3 3 0 109.88 9.88" />
    </svg>
  );
}

/** Corner brackets framing the viewfinder. */
function ViewfinderCorners() {
  const L = 24;
  const T = 3;
  const style = { background: 'white', borderRadius: 2 };
  return (
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute top-0 left-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute top-0 left-0" style={{ width: T, height: L, ...style }} />
      <div className="absolute top-0 right-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute top-0 right-0" style={{ width: T, height: L, ...style }} />
      <div className="absolute bottom-0 left-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute bottom-0 left-0" style={{ width: T, height: L, ...style }} />
      <div className="absolute bottom-0 right-0" style={{ width: L, height: T, ...style }} />
      <div className="absolute bottom-0 right-0" style={{ width: T, height: L, ...style }} />
    </div>
  );
}

export function BarcodeScannerModal({
  open,
  onScan,
  onClose,
}: {
  open: boolean;
  /** Called once with the normalized barcode when a frame decodes. */
  onScan: (barcode: string) => void;
  onClose: () => void;
}) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  // html5-qrcode looks the container up by id, so it must already be in the
  // DOM before the instance is constructed. This ref is how we know it is.
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'initializing' | 'scanning' | 'found' | 'error'>('initializing');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastBarcode, setLastBarcode] = useState<string | null>(null);

  // Held in refs so the html5-qrcode callback never reads a stale closure.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const handledRef = useRef(false);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try {
        await scanner.stop();
        scanner.clear();
      } catch {
        // Already stopped — nothing to unwind.
      }
    }
  }, []);

  const startScanner = useCallback(async () => {
    setState('initializing');
    setErrorMessage(null);
    handledRef.current = false;

    if (!navigator.mediaDevices?.getUserMedia) {
      setState('error');
      setErrorMessage('Camera not supported in this browser. Use a USB barcode scanner or type the barcode.');
      return;
    }

    // The container must be mounted first — html5-qrcode resolves it by id and
    // throws "HTML Element with id=... not found" otherwise.
    if (!containerRef.current) return;

    // Retry path: discard any previous instance before building a new one, or
    // html5-qrcode throws about the element already being in use.
    await stopScanner();

    try {
      const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
        formatsToSupport: SUPPORTED_FORMATS,
        verbose: false,
      });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1, disableFlip: false },
        (decodedText) => {
          // One barcode per open — ignore later frames while we unwind.
          if (handledRef.current) return;
          if (!isAcceptableScan(decodedText)) return;
          handledRef.current = true;

          const code = normalizeScannedBarcode(decodedText);
          setLastBarcode(code);
          setState('found');
          try {
            navigator.vibrate?.(100);
          } catch {
            // Vibration unavailable — cosmetic only.
          }

          // Brief confirmation flash, then hand the code back and close.
          setTimeout(() => {
            onScanRef.current(code);
            onCloseRef.current();
          }, 400);
        },
        () => {
          // Frame without a barcode — the normal case, not an error.
        },
      );

      setState('scanning');
    } catch (err) {
      setState('error');
      setErrorMessage(cameraErrorMessage(err));
    }
  }, [stopScanner]);

  useEffect(() => {
    if (!open) return;
    // `open` gates an early `return null`, so on the render that opens the
    // modal this effect is the first point at which the container exists.
    // A rAF defers past the browser's paint, which html5-qrcode needs in order
    // to measure the element it injects the <video> into.
    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      if (!cancelled) void startScanner();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      void stopScanner();
    };
  }, [open, startScanner, stopScanner]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Barcode scanner"
    >
      <div className="absolute top-4 right-4 z-10">
        <Button
          variant="ghost"
          size="md"
          onClick={onClose}
          className="h-12 w-12 rounded-full border-0 bg-white/10 text-white hover:bg-white/20"
          aria-label="Close scanner"
        >
          <CloseIcon />
        </Button>
      </div>

      <div className="flex w-full max-w-[520px] flex-col items-center px-4">
        {/* The viewfinder container stays mounted for the whole time the modal
            is open — html5-qrcode resolves it by id, so unmounting it on error
            would make a retry impossible. On error it is only hidden. */}
        <div className={cn('relative', state === 'error' && 'hidden')}>
          {/* html5-qrcode injects the <video> element here. */}
          <div
            ref={containerRef}
            id={SCANNER_ELEMENT_ID}
            className="h-[280px] w-[280px] overflow-hidden rounded-2xl md:h-[320px] md:w-[320px]"
          />
          <ViewfinderCorners />
          {state === 'scanning' && (
            <div className="pointer-events-none absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-success-400 to-transparent opacity-80 animate-scan-line" />
          )}
          {state === 'found' && (
            <div className="pointer-events-none absolute inset-0 animate-pulse rounded-2xl bg-success-400/10 ring-4 ring-success-400" />
          )}
        </div>

        {state === 'error' ? (
          <div className="flex max-w-sm flex-col items-center text-center">
            <CameraOffIcon />
            <p className="mt-4 text-sm font-medium text-white/90">{errorMessage}</p>
            <div className="mt-6 flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void startScanner()}
                className="border-0 bg-white/15 text-white hover:bg-white/25"
              >
                Try Again
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={onClose}
                className="border-0 bg-white/15 text-white hover:bg-white/25"
              >
                Enter Barcode Manually
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 text-center">
            {state === 'initializing' && <p className="text-sm text-white/60">Starting camera…</p>}
            {state === 'scanning' && (
              <p className="text-sm text-white/70">Point the camera at the product barcode</p>
            )}
            {state === 'found' && lastBarcode && (
              <p className="font-mono text-sm font-medium tabular-nums text-success-400">
                {lastBarcode}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
